import type { ReactNode } from 'react';
import { useAppSelector } from '@/store/hooks';
import { selectTenantModules, type TenantModule } from '@/store/slices/tenantSlice';
import { PageLoader } from '@/components/ui/feedback/page-loader';
import NotFoundPage from '@/pages/NotFoundPage';

interface ModuleGuardProps {
  requiredModule: TenantModule | TenantModule[];
  children: ReactNode;
}

/**
 * Guards content behind a tenant module check.
 *
 * - While modules haven't loaded yet (empty array), shows a loader.
 * - If any required module is present, renders children.
 * - If none of the required modules are present, renders the 404 page.
 */
export function ModuleGuard({ requiredModule, children }: ModuleGuardProps) {
  const modules = useAppSelector(selectTenantModules);

  if (modules.length === 0) {
    return <PageLoader />;
  }

  const required = Array.isArray(requiredModule) ? requiredModule : [requiredModule];
  if (!required.some((module) => modules.includes(module))) {
    return <NotFoundPage />;
  }

  return <>{children}</>;
}
